import ISelection from '@models/betSlip/ISelection';
import { BetSlipType } from '@models/shared/betSlip/betSlipEnums';
import { MUTATION_TYPE } from '@store/modules/ui/betSlip/mutations';
import {
    BetSlipContext,
    SelectionAddResult,
} from '@store/modules/ui/betSlip/types';
import {
    findSelectionIndex,
    findSelectionIndexByEventId,
} from '@store/modules/ui/shared/betSlip/Helpers';

import { Selection } from '@superbet-group/betting.lib.validation';
import { initAllowances } from './helpers/allowances';
import { SelectionSourceScreen } from '@superbet-group/web.types';
import { betBuilderAnalyticsStore } from '@sportOfferFederation/bet-builder/store/analytics';
import {
    logBetslipEventAddEvent,
    logBetslipEventRemoveEvent,
} from '@src/app/utils/analyticsEvents';

const superbetsBetslipEventAddSources: SelectionSourceScreen[] = [
    SelectionSourceScreen.SportsCalendarPopularSuperbets,
    SelectionSourceScreen.HomePopularSuperbets,
];

/*
(1) If you select a different outcome for a market line you already have in the BetSlip
    1. **Final** from 1 to 2 ⇒ **SWAP**
    2. **Total Goals** from [Over 2.5] to [Under 2.5] ⇒ **SWAP**
(2) If you select a market that is not combinable with some market you already have in the BetSlip (e.g. you selected **Total goals** but now you want to add **Final**) ⇒ **SWAP**
(3) If you select a market that is combinable with some market you already have
    1. selected **Cards** but now you want to add **Corners** ⇒ **ADD** (markets are combinable)
    2. selected **Total player points LeBron** [Over 22.5], and then **Total player points Stephen Curry** [Under 25.5] ⇒ **ADD** (market is combinable with itself)
(4) If you have two markets already combined in the BetSlip (e.g. Corners and Cards)
    1. You can select a third market that **is allowed** to be combined with the first two (e.g. Offsides) ⇒ **ADD**
    2. (Same as 2.a.) You can select a third market that **is not allowed** to be combined (e.g. Final) ⇒ **SWAP**
*/
export function createBetSlipSelectionsHelpers(
    maxSystemsCount: number,
    areMarketsAllowed: (market1: Selection, market2: Selection) => boolean,
) {
    /**
     * Since the selection is triggered to be added to the BetSlip by calling the toggleSelection action from the betslip store, the analytics events are only possible to be called here.
     */
    function _betbuilderAnalyticsHandler(context: any, { sourceScreen: source, odd, event, index }: ISelection) {
        // check if this selection is of correct source
        if (superbetsBetslipEventAddSources.includes(source)) {
            // check if this selection has the necessary props
            if (!event || !odd || typeof index === 'undefined') {
                return;
            }

            betBuilderAnalyticsStore.actions.addSuperbetToBetslip(context, { eventId: event.id, odd, source, index });
        }
    }

    async function handleSelectionToggle(
        context: BetSlipContext,
        { selection, index }: { selection: ISelection; index?: number },
    ): Promise<SelectionAddResult> {
        const { commit, state } = context;

        const sameEventSelections = getSameEventSelections(state.selections, selection);

        // 1. First selection for an event, just add it
        if (sameEventSelections.length === 0) {
            return addSelection(selection, { index, isFirstAdd: true });
        }

        // 2. There is an existing selection for the same event
        const isExactSameSelection = sameEventSelections.some((s) => s.isEqual(selection));
        // 2.1. Toggle the exact same odd (0)
        if (isExactSameSelection) {
            return removeSelection(selection);
        }

        // 2.2. Swap within same market line (1)
        const selectionInSameMarketLine = sameEventSelections.find((s) => isSameMarketLine(selection, s));
        if (selectionInSameMarketLine) {
            return swapSelection(selection, selectionInSameMarketLine);
        }

        await initAllowances();

        // 3. Remove all non-combinable markets and then add (2) (3) (4)
        sameEventSelections
            .filter(
                (existingSelection) =>
                    !areMarketsAllowed(
                        {
                            marketId: selection.getMarketId(),
                            odd: selection.odd,
                            isOngoing: selection.isOngoing(),
                        } as unknown as Selection,
                        {
                            marketId: existingSelection.getMarketId(),
                            odd: existingSelection.odd,
                            isOngoing: existingSelection.isOngoing(),
                        } as unknown as Selection,
                    ),
            )
            .forEach(removeSelection);

        return addSelection(selection, { index: undefined, isFirstAdd: false });

        function swapSelection(selection: ISelection, selectionInSameMarketLine: ISelection): SelectionAddResult {
            logBetslipEventAddEvent(selection);
            _betbuilderAnalyticsHandler(context, selection);
            commit(MUTATION_TYPE.UPDATE_SELECTION, {
                selection,
                index: findSelectionIndex(state.selections, selectionInSameMarketLine),
            });
            return SelectionAddResult.updated;
        }

        function removeSelection(selection: ISelection): SelectionAddResult {
            logBetslipEventRemoveEvent(selection);
            commit(MUTATION_TYPE.REMOVE_SELECTION, {
                index: findSelectionIndex(state.selections, selection),
            });
            return SelectionAddResult.removed;
        }

        function addSelection(selection: ISelection, { index, isFirstAdd }: { index?: number; isFirstAdd: boolean }): SelectionAddResult {
            logBetslipEventAddEvent(selection);
            _betbuilderAnalyticsHandler(context, selection);
            commit(MUTATION_TYPE.ADD_SELECTION, { selection, index });
            limitMaxSystems(context, selection);
            return isFirstAdd ? SelectionAddResult.added : SelectionAddResult.addedAnother;
        }
    }

    // This is the old implementation which will be used with the feature toggle
    function handleSelectionToggleLegacy(
        context: BetSlipContext,
        { selection, index }: { selection: ISelection; index?: number },
    ): SelectionAddResult {
        const { commit, state } = context;
        const existingSelectionIndex = findSelectionIndexByEventId(
            state.selections,
            selection.getEventId(),
            selection.getItemType(),
        );

        if (existingSelectionIndex !== -1) {
            if (state.selections[existingSelectionIndex].isEqual(selection)) {
                logBetslipEventRemoveEvent(selection);
                commit(MUTATION_TYPE.REMOVE_SELECTION, { index: existingSelectionIndex });
                return SelectionAddResult.removed;
            }
            logBetslipEventAddEvent(selection);
            _betbuilderAnalyticsHandler(context, selection);
            commit(MUTATION_TYPE.UPDATE_SELECTION, { selection, index: existingSelectionIndex });
            return SelectionAddResult.updated;
        }
        logBetslipEventAddEvent(selection);
        _betbuilderAnalyticsHandler(context, selection);
        commit(MUTATION_TYPE.ADD_SELECTION, { selection, index });
        limitMaxSystems(context, selection);
        return SelectionAddResult.added;
    }

    function getNumberOfSelectionsEligibleForSystems(context: BetSlipContext) {
        return context.getters.selections.filter((s) => !s.isFixed).length;
    }

    // assure there are [maxSystemsCount] systems max
    function limitMaxSystems(context: BetSlipContext, selectionToAdd?: ISelection): void {
        if (context.state.type !== BetSlipType.system) {
            return;
        }

        let notFixedCount = getNumberOfSelectionsEligibleForSystems(context);
        if (notFixedCount <= maxSystemsCount) {
            return;
        }

        if (selectionToAdd) {
            // eslint-disable-next-line no-param-reassign
            selectionToAdd.isFixed = true;
        } else {
            context.getters.selections.forEach((s) => {
                if (notFixedCount === maxSystemsCount) {
                    return;
                }
                if (!s.isFixed) {
                    notFixedCount -= 1;
                    context.commit(MUTATION_TYPE.SET_SELECTION_IS_FIXED, {
                        selection: s,
                        isFixed: true,
                    });
                }
            });
        }
    }

    return {
        handleSelectionToggle,
        handleSelectionToggleLegacy,
        limitMaxSystems,
        getNumberOfSelectionsEligibleForSystems,
    };
}

function isSameMarketLine(selection: ISelection, other: ISelection): boolean {
    return (
        selection.getMarketId() === other.getMarketId()
        && selection.getOddSpecialBetValue() === other.getOddSpecialBetValue()
    );
}

function getSameEventSelections(selections: ISelection[], selection: ISelection): ISelection[] {
    return selections
        .filter((s) => s.getEventId() === selection.getEventId());
}
